import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { passwordValidation } from '../../shared-functions/Validation';
import accountApi from '../../utils/api/account';
import MyButton from '../button/Button';
import CustomInput from '../custom-input/CustomInput';
import ModalBox from '../modal-box/ModalBox';
import SuccessNotification from '../success-notification/SuccessNotification';

interface ResetPasswordModalProperties {
  open: boolean;
  onClose: () => void;
}

interface PasswordFields {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Renders the profile password reset dialog and its validation success popup.
 * @param {ResetPasswordModalProperties} properties - Modal visibility and close handler.
 * @returns {React.JSX.Element} The password reset modal.
 */
function ResetPasswordModal({
  open,
  onClose,
}: Readonly<ResetPasswordModalProperties>): React.JSX.Element {
  const { t } = useTranslation('profile');
  const dispatch = useDispatch();
  const [passwordFields, setPasswordFields] = useState<PasswordFields>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showErrors, setShowErrors] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  /**
   * Updates a password field when its input value changes.
   * @param {string} name - Name of the password field to update.
   * @param {string | number} value - New field value.
   * @returns {void}
   */
  const handlePasswordChange = (name: string, value: string | number): void => {
    setPasswordFields((previous) => ({
      ...previous,
      [name]: String(value),
    }));
  };

  /**
   * Closes the modal and clears values and validation state.
   * @returns {void}
   */
  const handleClose = (): void => {
    setPasswordFields({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setShowErrors(false);
    setShowSuccess(false);
    onClose();
  };

  /**
   * Validates the password fields and displays the success popup when valid.
   * @returns {void}
   */
  const handleSubmit = (): void => {
    setShowErrors(true);
    if (
      !passwordFields.oldPassword ||
      !passwordValidation(passwordFields.newPassword) ||
      passwordFields.newPassword === passwordFields.oldPassword ||
      passwordFields.confirmPassword !== passwordFields.newPassword
    ) {
      return;
    }

    accountApi
      .passwordUpdated(passwordFields)
      .then(() => {
        setShowSuccess(true);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || error.message,
            status: true,
            type: 'error',
          }),
        );
      });
  };

  const hasOldPasswordError = showErrors && !passwordFields.oldPassword;
  const hasNewPasswordError =
    showErrors &&
    (!passwordFields.newPassword ||
      !passwordValidation(passwordFields.newPassword) ||
      passwordFields.newPassword === passwordFields.oldPassword);
  const hasConfirmPasswordError =
    showErrors &&
    (!passwordFields.confirmPassword ||
      passwordFields.confirmPassword !== passwordFields.newPassword);

  let newPasswordErrorMessage;

  if (!passwordFields.newPassword) {
    newPasswordErrorMessage = t('passwordRequired');
  } else if (passwordFields.newPassword === passwordFields.oldPassword) {
    newPasswordErrorMessage = t('newPasswordMustDiffer');
  } else {
    newPasswordErrorMessage = t('passwordValidationRequirements');
  }
  const confirmPasswordErrorMessage = !passwordFields.confirmPassword
    ? t('confirmPasswordRequired')
    : t('passwordAndConfirmMustMatch');

  return (
    <>
      <ModalBox
        open={open && !showSuccess}
        title={t('changePassword')}
        content={
          <div className="reset-password-modal-content">
            <CustomInput
              inputLabel={t('oldPassword')}
              type="password"
              placeholder={t('enterOldPassword')}
              name="oldPassword"
              value={passwordFields.oldPassword}
              onChangeValue={handlePasswordChange}
              error={hasOldPasswordError}
              errorMessage={t('oldPasswordRequired')}
              valueRequired
            />
            <CustomInput
              inputLabel={t('newPassword')}
              type="password"
              placeholder={t('enterNewPassword')}
              name="newPassword"
              value={passwordFields.newPassword}
              onChangeValue={handlePasswordChange}
              error={hasNewPasswordError}
              errorMessage={newPasswordErrorMessage}
              valueRequired
            />
            <CustomInput
              inputLabel={t('confirmPassword')}
              type="password"
              placeholder={t('enterConfirmPassword')}
              name="confirmPassword"
              value={passwordFields.confirmPassword}
              onChangeValue={handlePasswordChange}
              error={hasConfirmPasswordError}
              errorMessage={confirmPasswordErrorMessage}
              valueRequired
            />
            <div className="reset-password-modal-actions">
              <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleClose} />
              <MyButton
                label={t('changePassword')}
                buttonType="submit"
                onClickFunc={handleSubmit}
              />
            </div>
          </div>
        }
        closeModal={handleClose}
        header
      />
      <ModalBox
        open={open && showSuccess}
        title={null}
        content={
          <SuccessNotification
            title={t('passwordChangedSuccessfully')}
            description={t('yourPasswordHasBeenChanged')}
            notificationCloseTimer={3000}
            onNotificationClose={handleClose}
          />
        }
        closeModal={handleClose}
        header={false}
        notification
      />
    </>
  );
}

export default ResetPasswordModal;
